import { zodResolver } from "@hookform/resolvers/zod";
import { UserFormData } from "@repo/types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useCreateUser, useUpdateUser, useUser } from "../../../hooks/api/user";
import {
  userCreateFormSchema,
  userUpdateFormSchema,
  UserFormSchema,
} from "./user-form-schema";

interface UseUserFormProps {
  userId?: number;
  onSuccess: () => void;
}

type UserFormValues = UserFormData & { confirmPassword?: string };

function extractFieldErrors(errorResponse: {
  errors?: Record<string, unknown>;
  field?: string;
  message?: string;
}): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  if (errorResponse.errors) {
    Object.entries(errorResponse.errors).forEach(([field, value]) => {
      if (typeof value === "string") fieldErrors[field] = value;
    });
  }
  if (errorResponse.field && errorResponse.message) {
    fieldErrors[errorResponse.field] = errorResponse.message;
  }
  return fieldErrors;
}

export function useUserForm({ userId, onSuccess }: UseUserFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const isEditMode = !!userId;

  const { data: userData, isLoading: isUserLoading } = useUser(userId || 0);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const resolverSchema = isEditMode ? userUpdateFormSchema : userCreateFormSchema;

  const form = useForm<UserFormValues>({
    resolver: zodResolver(resolverSchema) as never,
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      profil_id: 0,
      groupe_id: 0,
      is_active: true,
      email_active: false,
    },
  });

  useEffect(() => {
    if (!isEditMode || !userData?.data) return;
    form.reset({
      username: userData.data.username ?? "",
      email: userData.data.email ?? "",
      password: "",
      confirmPassword: "",
      profil_id: userData.data.profil_id,
      groupe_id: userData.data.groupe_id,
      is_active: userData.data.is_active,
      email_active: userData.data.email_active,
    });
  }, [isEditMode, userData, form]);

  useEffect(() => {
    const subscription = form.watch(() => {
      if (Object.keys(fieldErrors).length > 0) setFieldErrors({});
      if (error) setError(null);
    });
    return () => subscription.unsubscribe();
  }, [form, fieldErrors, error]);

  const onSubmit = async (data: UserFormValues) => {
    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      const { confirmPassword, ...submitData } = data;
      if (isEditMode && userId) {
        const updateData = { ...submitData };
        if (!updateData.password) delete updateData.password;
        await updateUser.mutateAsync({
          id: userId,
          data: { id: userId, ...updateData },
        });
      } else {
        await createUser.mutateAsync(submitData);
      }
      onSuccess();
    } catch (err) {
      const apiError = err as {
        response?: {
          data?: {
            message?: string;
            errors?: Record<string, unknown>;
            field?: string;
          };
        };
        message?: string;
      };
      const errorResponse = apiError.response?.data;
      if (errorResponse) {
        if (errorResponse.message) setError(errorResponse.message);
        const nextFieldErrors = extractFieldErrors(errorResponse);
        Object.entries(nextFieldErrors).forEach(([field, message]) => {
          form.setError(field as keyof UserFormValues, { type: "manual", message });
        });
        setFieldErrors(nextFieldErrors);
      } else {
        setError(
          apiError.message ||
            "Une erreur s'est produite lors de l'enregistrement de l'utilisateur"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isEditMode && isUserLoading;

  return {
    form,
    isEditMode,
    isSubmitting,
    isLoading,
    error,
    fieldErrors,
    onSubmit,
  };
}

export type { UserFormSchema };
